import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FileParameter } from '../../../sdk/sdk';
import { AttachmentService } from '../../../services/attachment.service';
import { FilePreviewService } from '../../../services/file-preview.service';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'app-attachment-upload-failure-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, StateMessageComponent],
  styleUrls: ['./attachment-upload-failure-modal.component.scss'],
  templateUrl: './attachment-upload-failure-modal.component.html',
})
export class AttachmentUploadFailureModalComponent implements OnDestroy {
  private readonly attachmentService = inject(AttachmentService);
  private readonly modalService = inject(ModalService);
  private readonly filePreviewService = inject(FilePreviewService);
  readonly activeModal = inject(NgbActiveModal);
  private readonly previewUrls = new Set<string>();

  @Input() failedFiles: File[] = [];
  @Input() opportunityId = '';
  @Input() groupId: string | null = null;
  @Input() description = '';
  @Input() sectionId: string | null = null;

  readonly uploading = signal(false);
  readonly uploadError = signal<string | null>(null);

  onRetry(): void {
    if (this.failedFiles.length === 0) {
      return;
    }

    this.uploading.set(true);
    this.uploadError.set(null);

    const uploads = this.failedFiles.map((file) => {
      const fileParameter: FileParameter = {
        data: file,
        fileName: file.name,
      };
      return this.attachmentService
        .uploadAttachment(
          this.opportunityId,
          fileParameter,
          this.groupId,
          this.description,
          this.sectionId
        )
        .pipe(
          catchError((error) => {
            console.error('Failed to upload file:', error);
            return of(null);
          })
        );
    });

    forkJoin(uploads).subscribe({
      next: (results) => {
        this.uploading.set(false);
        const successCount = results.filter((r) => r !== null).length;
        if (successCount === this.failedFiles.length) {
          // All retries succeeded, close this modal and show success modal
          this.activeModal.close({ success: true });
          this.modalService.openAttachmentUploadSuccessModal();
        } else {
          // Some still failed, update the failed files list
          const stillFailed: File[] = [];
          for (let index = 0; index < results.length; index++) {
            const result = results[index];
            if (result === null) {
              stillFailed.push(this.failedFiles[index]);
            }
          }
          this.failedFiles = stillFailed;
        }
      },
      error: (error) => {
        this.uploading.set(false);
        this.uploadError.set('An unexpected error occurred during upload.');
        console.error('Upload error:', error);
      },
    });
  }

  onRemoveFile(file: File): void {
    this.failedFiles = this.failedFiles.filter((f) => f !== file);
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  getFilePreviewUrl(file: File): string {
    const url = this.filePreviewService.createPreviewUrl(file);
    if (url) {
      this.previewUrls.add(url);
    }
    return url ?? '';
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach((url) => this.filePreviewService.revokePreviewUrl(url));
    this.previewUrls.clear();
  }
}
